import type { PageFilters } from './page-filters.js';
import { DEFAULT_DAYS, RANGES } from './ranges.js';

/**
 * The Daily page's range and filters live in the address so a view can be
 * bookmarked or sent: /daily?days=7&account=acct-a&rep=Alfa. Pure, so the
 * round trip is tested without a browser. A value that is not one of the
 * known ranges falls back to the default rather than asking the server for
 * a window nobody offered.
 */
export interface DailyUrlState {
  days: number;
  filters: PageFilters;
}

const FILTER_KEYS = ['account', 'rep', 'provider', 'feedId', 'country'] as const;

export function parseDailyUrl(search: string): DailyUrlState {
  const p = new URLSearchParams(search);
  const raw = p.get('days');
  const n = raw == null ? NaN : Number(raw);
  const days = RANGES.some((r) => r.days === n) ? n : DEFAULT_DAYS;
  const filters: PageFilters = {};
  for (const k of FILTER_KEYS) {
    const v = p.get(k)?.trim();
    if (v) filters[k] = v;
  }
  return { days, filters };
}

/** The query string for a state ('' for the defaults), without the leading "?" unless there is something to say. */
export function dailySearch(state: DailyUrlState): string {
  const p = new URLSearchParams();
  if (state.days !== DEFAULT_DAYS) p.set('days', String(state.days));
  for (const k of FILTER_KEYS) {
    const v = state.filters[k];
    if (v) p.set(k, v);
  }
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** The query the server takes (/api/daily, /api/data): the window and the filters, blanks left out. */
export function dailyQuery(from: string, to: string, filters: PageFilters): string {
  const p = new URLSearchParams({ from, to });
  for (const k of FILTER_KEYS) {
    const v = filters[k];
    if (v) p.set(k, v);
  }
  return p.toString();
}
